(() => {
  if (customElements.get('virtual-gallery-showcase')) return;

  const GALLERY_RESOURCE_ORIGINS = [
    'https://visit.virtualartgallery.com',
    'https://gallery.arilyn.fi',
    'https://arilyn-gallery-production-uploads.s3.amazonaws.com',
    'https://arilyn-gallery-production-data.s3.amazonaws.com',
  ];
  const GALLERY_API_ORIGIN = 'https://gallery.arilyn.fi';
  const GALLERY_MODEL_ORIGIN = 'https://arilyn-gallery-production-data.s3.amazonaws.com';
  const STANDALONE_PRELOAD_DELAY = 80;

  class VirtualGalleryShowcase extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.preloadedViewer = null;
      this.standalonePreloadTimer = null;
      this.handleStandaloneVisibilityChange = null;
      this.modelWarmupController = null;
      this.handleDocumentFullscreenChange = () => this.syncFullscreenState();
      this.handleDocumentKeydown = (event) => {
        if (event.key !== 'Escape') return;
        const viewer = this.querySelector('[data-gallery-viewer].is-pseudo-fullscreen');
        if (viewer) this.exitFullscreen(viewer);
      };
      this.addPreconnect();

      this.querySelectorAll('.js-vg-launch').forEach((button) => {
        button.addEventListener('click', () => this.openGallery(button));
        button.addEventListener('pointerdown', () => this.preloadById(button.dataset.galleryLaunch, 'direct-intent'), { passive: true });
        ['mouseenter', 'focusin'].forEach((eventName) => {
          button.addEventListener(eventName, () => this.preloadById(button.dataset.galleryLaunch, 'intent'), { passive: true });
        });
      });

      this.querySelectorAll('[data-gallery-nav]').forEach((link) => {
        ['mouseenter', 'focusin'].forEach((eventName) => {
          link.addEventListener(eventName, () => this.preloadById(link.dataset.galleryNav, 'intent'), { passive: true });
        });
      });

      this.querySelectorAll('.js-vg-close').forEach((button) => {
        button.addEventListener('click', () => this.closeGallery(button.dataset.galleryClose, true));
      });

      this.querySelectorAll('.js-vg-fullscreen').forEach((button) => {
        button.addEventListener('click', () => this.toggleFullscreen(button.dataset.galleryFullscreen));
      });

      this.querySelectorAll('.js-vg-toolbar-toggle').forEach((button) => {
        button.addEventListener('click', () => this.toggleToolbar(button.dataset.galleryToolbarToggle));
      });

      this.querySelectorAll('[data-gallery-iframe]').forEach((iframe) => {
        iframe.addEventListener('load', () => {
          if (iframe.getAttribute('src') === 'about:blank') return;
          const viewer = iframe.closest('[data-gallery-viewer]');
          if (!viewer) return;
          viewer.dataset.iframeLoadCount = String(Number(viewer.dataset.iframeLoadCount || 0) + 1);
          this.markViewerDocumentLoaded(viewer);
        });
      });

      document.addEventListener('fullscreenchange', this.handleDocumentFullscreenChange);
      document.addEventListener('keydown', this.handleDocumentKeydown);

      if ('IntersectionObserver' in window) {
        this.navigationObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.querySelectorAll('[data-gallery-nav]').forEach((link) => link.removeAttribute('aria-current'));
            const active = this.querySelector(`[data-gallery-nav="${entry.target.dataset.gallerySection}"]`);
            if (active) active.setAttribute('aria-current', 'location');
          });
        }, { rootMargin: '-35% 0px -55% 0px', threshold: 0 });
        this.querySelectorAll('[data-gallery-section]').forEach((gallery) => this.navigationObserver.observe(gallery));

        if (this.connectionAllowsAutomaticPreload()) {
          this.preloadObserver = new IntersectionObserver((entries) => {
            const candidate = entries.find((entry) => entry.isIntersecting);
            if (candidate) this.preloadViewer(candidate.target, 'automatic');
          }, { rootMargin: '650px 0px', threshold: 0.01 });
          this.querySelectorAll('[data-gallery-viewer]').forEach((viewer) => this.preloadObserver.observe(viewer));
        }
      }

      if (this.hasAttribute('data-single-gallery')) this.scheduleStandalonePreload();
    }

    disconnectedCallback() {
      this.navigationObserver?.disconnect();
      this.preloadObserver?.disconnect();
      if (this.standalonePreloadTimer) window.clearTimeout(this.standalonePreloadTimer);
      this.modelWarmupController?.abort();
      if (this.handleStandaloneVisibilityChange) {
        document.removeEventListener('visibilitychange', this.handleStandaloneVisibilityChange);
      }
      document.removeEventListener('fullscreenchange', this.handleDocumentFullscreenChange);
      document.removeEventListener('keydown', this.handleDocumentKeydown);
    }

    addPreconnect() {
      GALLERY_RESOURCE_ORIGINS.forEach((origin) => {
        const hasPreconnect = [...document.head.querySelectorAll('link[rel="preconnect"]')]
          .some((link) => link.href === `${origin}/` || link.href === origin);
        if (!hasPreconnect) {
          const link = document.createElement('link');
          link.rel = 'preconnect';
          link.href = origin;
          link.crossOrigin = 'anonymous';
          link.dataset.vgPreconnect = '';
          document.head.appendChild(link);
        }

        const dnsHref = `//${new URL(origin).host}`;
        const hasDnsPrefetch = [...document.head.querySelectorAll('link[rel="dns-prefetch"]')]
          .some((link) => link.getAttribute('href') === dnsHref);
        if (!hasDnsPrefetch) {
          const dnsPrefetch = document.createElement('link');
          dnsPrefetch.rel = 'dns-prefetch';
          dnsPrefetch.href = dnsHref;
          dnsPrefetch.dataset.vgPreconnect = '';
          document.head.appendChild(dnsPrefetch);
        }
      });
    }

    async preloadRoomModel(viewer) {
      const source = viewer?.querySelector('[data-gallery-iframe]')?.dataset.src;
      const projectId = this.galleryProjectId(source);
      if (!projectId) return;

      this.modelWarmupController?.abort();
      this.modelWarmupController = new AbortController();

      try {
        const response = await fetch(`${GALLERY_API_ORIGIN}/gallery/project/${projectId}/export-draft/`, {
          mode: 'cors',
          credentials: 'omit',
          referrerPolicy: 'strict-origin-when-cross-origin',
          signal: this.modelWarmupController.signal,
        });
        if (!response.ok) return;

        const modelUrl = this.findRoomModelUrl(await response.json());
        if (!modelUrl || new URL(modelUrl).origin !== GALLERY_MODEL_ORIGIN) return;
        if ([...document.head.querySelectorAll('link[data-vg-model-preload]')].some((link) => link.href === modelUrl)) return;

        const preload = document.createElement('link');
        preload.rel = 'preload';
        preload.as = 'fetch';
        preload.href = modelUrl;
        preload.crossOrigin = 'anonymous';
        preload.fetchPriority = 'low';
        preload.dataset.vgModelPreload = '';
        preload.addEventListener('load', () => { this.dataset.modelPreload = 'ready'; }, { once: true });
        preload.addEventListener('error', () => { this.dataset.modelPreload = 'failed'; }, { once: true });
        this.dataset.modelPreload = 'requested';
        document.head.appendChild(preload);
      } catch (error) {
        if (error.name !== 'AbortError') this.dataset.modelPreload = 'unavailable';
      }
    }

    galleryProjectId(source) {
      if (!source) return '';
      try {
        const url = new URL(source, window.location.href);
        return url.pathname.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i)?.[0] || '';
      } catch (error) {
        return '';
      }
    }

    findRoomModelUrl(manifest) {
      const pending = [manifest];
      while (pending.length) {
        const value = pending.pop();
        if (typeof value === 'string' && /^https:\/\/[^\s]+\/templates\/[^\s]+\/model\.glb(?:\?[^\s]*)?$/i.test(value)) return value;
        if (Array.isArray(value)) pending.push(...value);
        else if (value && typeof value === 'object') pending.push(...Object.values(value));
      }
      return '';
    }

    publishedGallerySource(source) {
      if (!source) return '';
      try {
        const url = new URL(source, window.location.href);
        if (['0', 'false'].includes(url.searchParams.get('draft')?.toLowerCase())) {
          url.searchParams.delete('draft');
        }
        return url.toString();
      } catch (error) {
        return source;
      }
    }

    connectionAllowsIntentPreload() {
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (connection?.saveData || ['slow-2g', '2g', '3g'].includes(connection?.effectiveType)) return false;
      return window.matchMedia('(pointer: fine)').matches;
    }

    connectionAllowsAutomaticPreload() {
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (!connection || connection.saveData || connection.effectiveType !== '4g') return false;
      if (window.innerWidth < 990) return false;
      if (!window.matchMedia('(pointer: fine)').matches || document.visibilityState !== 'visible') return false;
      if (connection.downlink && connection.downlink < 5) return false;
      if (connection.rtt && connection.rtt > 150) return false;
      if (navigator.deviceMemory && navigator.deviceMemory < 4) return false;
      if (navigator.hardwareConcurrency && navigator.hardwareConcurrency < 4) return false;
      return true;
    }

    connectionAllowsStandalonePreload() {
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (connection?.saveData || ['slow-2g', '2g'].includes(connection?.effectiveType)) return false;
      if (document.visibilityState !== 'visible') return false;
      return true;
    }

    scheduleStandalonePreload() {
      const start = () => {
        if (!this.connectionAllowsStandalonePreload()) return;
        if (this.handleStandaloneVisibilityChange) {
          document.removeEventListener('visibilitychange', this.handleStandaloneVisibilityChange);
          this.handleStandaloneVisibilityChange = null;
        }

        const standaloneViewer = this.querySelector('[data-gallery-viewer]');
        if (!standaloneViewer) return;
        if (new URLSearchParams(window.location.search).get('vg_model_preload') !== '0') {
          this.preloadRoomModel(standaloneViewer);
        }
        this.standalonePreloadTimer = window.setTimeout(() => {
          this.preloadViewer(standaloneViewer, 'standalone');
        }, STANDALONE_PRELOAD_DELAY);
      };

      if (document.visibilityState === 'visible') {
        start();
        return;
      }

      this.handleStandaloneVisibilityChange = () => {
        if (document.visibilityState === 'visible') start();
      };
      document.addEventListener('visibilitychange', this.handleStandaloneVisibilityChange);
    }

    connectionAllowsDirectIntentPreload() {
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      return !connection?.saveData && !['slow-2g', '2g'].includes(connection?.effectiveType);
    }

    preloadById(id, mode = 'intent') {
      if (!id) return;
      if (mode === 'intent' && !this.connectionAllowsIntentPreload()) return;
      if (mode === 'automatic' && !this.connectionAllowsAutomaticPreload()) return;
      if (mode === 'direct-intent' && !this.connectionAllowsDirectIntentPreload()) return;
      this.preloadViewer(this.querySelector(`[data-gallery-viewer][data-gallery-id="${id}"]`), mode);
    }

    preloadViewer(viewer, mode = 'intent') {
      if (!viewer || viewer.classList.contains('is-active') || viewer.dataset.preloaded === 'true') return;
      if (this.querySelector('[data-gallery-viewer].is-active')) return;
      const iframe = viewer.querySelector('[data-gallery-iframe]');
      const shell = viewer.querySelector('[data-gallery-frame]');
      const source = this.publishedGallerySource(iframe?.dataset.src);
      if (!iframe || !shell || !source) return;

      if (this.preloadedViewer && this.preloadedViewer !== viewer && !this.preloadedViewer.classList.contains('is-active')) {
        this.unloadViewer(this.preloadedViewer);
      }

      viewer.dataset.preloaded = 'true';
      viewer.dataset.preloadMode = mode;
      this.preloadedViewer = viewer;
      shell.hidden = false;
      shell.classList.add('is-preloading');
      shell.setAttribute('aria-hidden', 'true');
      shell.setAttribute('inert', '');
      iframe.setAttribute('tabindex', '-1');
      iframe.loading = 'eager';
      shell.setAttribute('aria-busy', 'true');
      this.setViewerStatus(viewer, 'Preparing gallery…', 'loading');
      iframe.src = source;
    }

    markViewerDocumentLoaded(viewer) {
      const shell = viewer?.querySelector('[data-gallery-frame]');
      if (!viewer || !shell || viewer.dataset.preloaded !== 'true') return;
      viewer.dataset.iframeLoaded = 'true';
      shell.classList.add('is-loaded');
      shell.setAttribute('aria-busy', 'false');
      this.setViewerStatus(viewer, 'Gallery warming in the background', 'loaded');
    }

    setViewerStatus(viewer, status, state = 'idle') {
      const statusNode = viewer?.querySelector('[data-gallery-status]');
      const label = viewer?.querySelector('[data-gallery-button-label]');
      const launch = viewer?.querySelector('.js-vg-launch');
      const defaultLabel = label?.dataset.defaultLabel || label?.textContent || 'Enter the gallery';
      if (viewer) viewer.dataset.preloadState = state;
      if (statusNode) statusNode.textContent = status || statusNode.dataset.defaultText || '';
      if (label) {
        if (state === 'loading') label.textContent = 'Preparing gallery…';
        else label.textContent = defaultLabel;
      }
      if (launch) launch.setAttribute('aria-busy', String(state === 'loading'));
    }

    openGallery(button) {
      const id = button.dataset.galleryLaunch;
      const source = this.publishedGallerySource(button.dataset.src);
      if (!id || !source) return;

      this.querySelectorAll('[data-gallery-viewer][data-preloaded="true"]').forEach((viewer) => {
        if (viewer.dataset.galleryId !== id) this.resetViewer(viewer);
      });

      const viewer = this.querySelector(`[data-gallery-viewer][data-gallery-id="${id}"]`);
      const poster = viewer?.querySelector('[data-gallery-poster]');
      const shell = viewer?.querySelector('[data-gallery-frame]');
      const iframe = viewer?.querySelector('[data-gallery-iframe]');
      if (!viewer || !shell || !iframe) return;

      viewer.classList.add('is-active');
      if (poster) poster.hidden = true;
      shell.hidden = false;
      shell.classList.remove('is-preloading');
      shell.removeAttribute('aria-hidden');
      shell.removeAttribute('inert');
      iframe.removeAttribute('tabindex');
      button.setAttribute('aria-expanded', 'true');
      this.setToolbarCollapsed(viewer, false);

      if (!viewer.dataset.preloaded || iframe.src === 'about:blank') {
        shell.classList.remove('is-loaded');
        shell.setAttribute('aria-busy', 'true');
        this.setViewerStatus(viewer, 'Preparing gallery…', 'loading');
        viewer.dataset.preloaded = 'true';
        iframe.loading = 'eager';
        iframe.src = source;
      }
      this.preloadedViewer = viewer;

      shell.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'center',
      });
    }

    closeGallery(id, restoreFocus = false) {
      const viewer = this.querySelector(`[data-gallery-viewer][data-gallery-id="${id}"]`);
      if (!viewer) return;
      const launch = viewer.querySelector('.js-vg-launch');
      const poster = viewer.querySelector('[data-gallery-poster]');
      const shell = viewer.querySelector('[data-gallery-frame]');
      if (viewer.classList.contains('is-fullscreen')) this.exitFullscreen(viewer);
      if (poster) poster.hidden = false;
      if (shell) shell.hidden = true;
      if (launch) launch.setAttribute('aria-expanded', 'false');
      this.setToolbarCollapsed(viewer, true);
      viewer.classList.remove('is-active');
      if (restoreFocus && launch) launch.focus();
    }

    toggleToolbar(id) {
      const viewer = this.querySelector(`[data-gallery-viewer][data-gallery-id="${id}"]`);
      const toolbar = viewer?.querySelector('[data-gallery-toolbar]');
      if (!viewer || !toolbar) return;
      this.setToolbarCollapsed(viewer, !toolbar.classList.contains('is-collapsed'));
    }

    setToolbarCollapsed(viewer, collapsed) {
      const toolbar = viewer?.querySelector('[data-gallery-toolbar]');
      const button = toolbar?.querySelector('.js-vg-toolbar-toggle');
      if (!toolbar || !button) return;
      toolbar.classList.toggle('is-collapsed', collapsed);
      button.setAttribute('aria-expanded', String(!collapsed));
      const label = button.querySelector('[data-toolbar-label]');
      if (label) label.textContent = collapsed ? 'Controls' : 'Hide controls';
    }

    async toggleFullscreen(id) {
      const viewer = this.querySelector(`[data-gallery-viewer][data-gallery-id="${id}"]`);
      if (!viewer?.classList.contains('is-active')) return;
      if (viewer.classList.contains('is-fullscreen') || document.fullscreenElement === viewer) {
        await this.exitFullscreen(viewer);
        return;
      }

      viewer.classList.add('is-fullscreen');
      document.documentElement.classList.add('mash-vg-fullscreen-lock');
      this.updateFullscreenControl(viewer, true);

      const useCssFullscreen = window.matchMedia('(max-width: 989px), (pointer: coarse)').matches;
      if (!useCssFullscreen && viewer.requestFullscreen) {
        try {
          await viewer.requestFullscreen({ navigationUI: 'hide' });
          viewer.classList.add('is-native-fullscreen');
          return;
        } catch (error) {
          // CSS fullscreen below preserves the already-loaded iframe when the
          // browser declines the native Fullscreen API.
        }
      }
      viewer.classList.add('is-pseudo-fullscreen');
    }

    async exitFullscreen(viewer) {
      if (!viewer) return;
      if (document.fullscreenElement === viewer && document.exitFullscreen) {
        try {
          await document.exitFullscreen();
        } catch (error) {
          // Clearing the local state below still restores the embedded viewer.
        }
      }
      this.clearFullscreen(viewer);
    }

    syncFullscreenState() {
      this.querySelectorAll('[data-gallery-viewer].is-native-fullscreen').forEach((viewer) => {
        if (document.fullscreenElement !== viewer) this.clearFullscreen(viewer);
      });
    }

    clearFullscreen(viewer) {
      viewer.classList.remove('is-fullscreen', 'is-native-fullscreen', 'is-pseudo-fullscreen');
      if (!this.querySelector('[data-gallery-viewer].is-fullscreen')) {
        document.documentElement.classList.remove('mash-vg-fullscreen-lock');
      }
      this.updateFullscreenControl(viewer, false);
    }

    updateFullscreenControl(viewer, expanded) {
      const button = viewer.querySelector('.js-vg-fullscreen');
      if (!button) return;
      button.setAttribute('aria-pressed', String(expanded));
      const label = button.querySelector('[data-fullscreen-label]');
      if (label) label.textContent = expanded ? 'Exit full screen' : 'Full screen';
    }

    unloadViewer(viewer) {
      const iframe = viewer?.querySelector('[data-gallery-iframe]');
      const shell = viewer?.querySelector('[data-gallery-frame]');
      if (iframe) iframe.src = 'about:blank';
      if (shell) {
        shell.hidden = true;
        shell.classList.remove('is-loaded', 'is-preloading');
        shell.setAttribute('aria-busy', 'false');
        shell.removeAttribute('aria-hidden');
        shell.removeAttribute('inert');
      }
      if (iframe) iframe.removeAttribute('tabindex');
      if (viewer) {
        delete viewer.dataset.preloaded;
        delete viewer.dataset.iframeLoaded;
        delete viewer.dataset.preloadMode;
        this.setViewerStatus(viewer, viewer.querySelector('[data-gallery-status]')?.dataset.defaultText || 'Immersive 3D exhibition', 'idle');
      }
      if (this.preloadedViewer === viewer) this.preloadedViewer = null;
    }

    resetViewer(viewer) {
      const poster = viewer.querySelector('[data-gallery-poster]');
      const shell = viewer.querySelector('[data-gallery-frame]');
      const launch = viewer.querySelector('.js-vg-launch');
      if (viewer.classList.contains('is-fullscreen')) this.exitFullscreen(viewer);
      this.unloadViewer(viewer);
      if (poster) poster.hidden = false;
      if (launch) launch.setAttribute('aria-expanded', 'false');
      viewer.classList.remove('is-active');
    }
  }

  customElements.define('virtual-gallery-showcase', VirtualGalleryShowcase);
})();
